import { message } from "antd";
import { AttachedFiles } from "legacyComponents/FileUploadContainer.types";
import {
    type ILineItemData,
    type ILineItemDefaults,
    type ILineItemResponse,
} from "legacyComponents/LineItemContainer.types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { generatePath, type RouteComponentProps } from "react-router";
import { useNavigate } from "react-router-dom-v5-compat";

import { useDiscussionsByExternalIdGetV2 } from "@btnet/services-btnet/handlers/discussions/discussions";
import { AllowanceCopyBehavior } from "@btnet/services-btnet/handlers/models/allowanceCopyBehavior";
import type { LineItem } from "@btnet/services-btnet/handlers/models/lineItem";
import type { ParsedUrlSelectionOptionFields } from "@btnet/services-btnet/handlers/models/parsedUrlSelectionOptionFields";
import type { SelectionOptionCreationRequest } from "@btnet/services-btnet/handlers/models/selectionOptionCreationRequest";
import type { SelectionOptionGroup } from "@btnet/services-btnet/handlers/models/selectionOptionGroup";
import { Statuses } from "@btnet/services-btnet/handlers/models/statuses";
import { useSelectionCatalogSaveItemsPostV2 } from "@btnet/services-btnet/handlers/selection-catalog/selection-catalog";
import {
    getSelectionOptionsBySelectionOptionIdGetV2QueryKey,
    useSelectionOptionsAttachmentsBySelectionOptionIdGetV2,
    useSelectionOptionsBySelectionOptionIdDeleteV2,
    useSelectionOptionsBySelectionOptionIdGetV2,
    useSelectionOptionsBySelectionOptionIdPutV2,
    useSelectionOptionsCopyOptionsPostV2,
    useSelectionOptionsCreateOptionPostV2,
    useSelectionOptionsParseFromUrlsPostV2,
    useSelectionOptionsSelectionOptionDefaultsByJobIdGetV2,
    useSelectionOptionsSelectionOptionsApprovePutV2,
    useSelectionOptionsSelectionOptionsDeclinePutV2,
    useSelectionOptionsSelectionOptionsResetPutV2,
    useSelectionOptionsSelectionOptionsSendPutV2,
} from "@btnet/services-btnet/handlers/selection-options/selection-options";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { useRequiredContext } from "helpers/globalContext/useRequiredContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";
import { queryClient } from "helpers/queryClient";
import { useWebviewContext } from "helpers/webview/WebviewContext";

import { type BTSelectItem } from "types/apiResponse/apiResponse";
import { BTLoginTypes, DiscussionsLinkedTypes } from "types/enum";

import { showApixErrorMessage } from "utilities/api/apix-handler";
import { showAPIErrorMessage } from "utilities/apiHandler";
import { routes } from "utilities/routes";

import {
    DiscussionsEntity,
    type IHasDiscussionContainer,
} from "commonComponents/entity/discussion/DiscussionContainer/DiscussionContainer.api.types";
import { withErrorBoundary } from "commonComponents/helpers/ErrorBoundary/ErrorBoundary";
import { withJobPrompt } from "commonComponents/helpers/JobPrompt/JobPrompt";
import { withJobsitesRequired } from "commonComponents/helpers/JobsitesRequired/JobsitesRequired";
import { BTLoading } from "commonComponents/utilities/BTLoading/BTLoading";
import {
    APPLY_PARSE_RESULT_TO_FORM_SUCCESS_MESSAGE,
    DEFAULT_SCRAPING_ERROR_MESSAGE,
} from "commonComponents/utilities/SelectionOption/SelectionOption.utils";

import { SelectionListContainerTabs } from "entity/selection/SelectionListContainer/SelectionListContainer.types";
import { SelectionOptionLineItem } from "entity/selectionChoice/SelectionChoice/SelectionChoice.api.types";
import { DeleteSelectionOption } from "entity/selectionOption/common/DeleteSelectionOption/DeleteSelectionOption";
import { ResetSelectionOptions } from "entity/selectionOption/common/ResetSelectionOption/ResetSelectionOptions";
import { ResetSelectionOptionConfirmDialog } from "entity/selectionOption/common/ResetSelectionOptionConfirmDialog/ResetSelectionOptionConfirmDialog";
import {
    buildSelectionOptionRequest,
    handleSelectionOptionActionMessages,
    refreshAndNavigateToViewMode,
    showStatusChangeConfirmation,
} from "entity/selectionOption/common/SelectionOption.utils";
import { useSelectionOptionRoutes } from "entity/selectionOption/common/useSelectionOptionRoutes";
import { showSaveToCatalogOutcomeMessage } from "entity/selectionOption/selectionOptionsOutcomeMessages";
import {
    mapCategoriesToBTSelectItems,
    mapLocationsToBTSelectItems,
} from "entity/selectionOption/selectionOptionsUtilities";
import { type JobTaxVersion } from "entity/tax/common/tax.types";

import {
    type ISelectionOptionAutofillState,
    type ISelectionOptionLocationState,
    type SelectionOptionStateOverride,
} from "./SelectionOption.types";
import {
    type ISelectionOptionFormValues,
    SelectionOptionPresentational,
    type SelectionOptionSaveAction,
} from "./SelectionOptionPresentational";

/**
 * Marks the costCodeOption whose id matches the cost code mapped to the selected allowance group
 * as `selected: true` (and clears `selected` on all others). Used so that picking an allowance on
 * the selection option form defaults the line-item cost code to the allowance's cost code.
 */
export const applyAllowanceCostCodeDefault = (
    lineItemDefaults: ILineItemDefaults | undefined,
    selectionOptionGroupId: number | null | undefined,
    costCodeIdsByGroupId: { [key: string]: number } | undefined,
): ILineItemDefaults | undefined => {
    if (!lineItemDefaults || !selectionOptionGroupId || !costCodeIdsByGroupId) {
        return lineItemDefaults;
    }
    const targetCostCodeId = costCodeIdsByGroupId[String(selectionOptionGroupId)];
    if (targetCostCodeId == null) {
        return lineItemDefaults;
    }
    const options = lineItemDefaults.costCodeOptions;
    if (!options || !Array.isArray(options.value)) {
        return lineItemDefaults;
    }
    return {
        ...lineItemDefaults,
        costCodeOptions: {
            ...options,
            value: options.value.map((item: { id?: number; selected?: boolean }) => ({
                ...item,
                selected: item.id === targetCostCodeId,
            })),
        },
    };
};

export interface ISelectionOptionEntity {
    description?: string;
    externalId?: string;
    externalBuilderId?: string;
    externalJobId?: string;
    /** Workflow status. Absent on catalog options (no approve/decline/reset flow). */
    status?: number | null;
    title?: string;
    lineItems?: ILineItemData;
    selectionOptionLineItems: SelectionOptionLineItem[];
    lineItemDefaults: ILineItemDefaults;
    /** Maps SelectionOptionGroupId → default CostCodeId from the related BTEstimate.
     *  Used by the presentational to default the line-item cost code when the form's
     *  selected allowance changes. */
    costCodeIdsByGroupId?: { [key: string]: number };
    selectionOptionGroups: SelectionOptionGroup[];
    selectionOptionGroupId?: number | null;
    categoryId?: number | null;
    categories?: BTSelectItem[];
    locationId?: number | null;
    locations?: BTSelectItem[];
    approvalByID?: number | null;
    approvalByName?: string | null;
    approvalDate?: Date | null;
    deadlineDate?: Date | null;
    addedByID?: number;
    addedByName?: string | null;
    addedByDate?: Date;
    canCreatePurchaseOrder?: boolean;
    canAddToExistingPurchaseOrder?: boolean;
    productUrl?: string;
    imageUrl?: string;
    taxGroupId?: number;
    effectiveTaxVersion?: JobTaxVersion;
    linkedChangeOrderStatus?: number | null;
}

export interface ISelectionOptionProps extends RouteComponentProps {
    entity: ISelectionOptionEntity;
    jobId: number;
    jobName?: string;
    selectionOptionId?: number;
    stateOverride?: SelectionOptionStateOverride;
    isModal?: boolean;
    selectionOptionGroupId?: number;
    onModalClose?: () => void;
    /**
     * Suppresses the Comments and Activity Log header actions (and skips the discussion fetch).
     * Used by SelectionOptionDetailPanel, which renders this option inside the allowance drawer
     * where collaboration/tracking affordances don't belong.
     */
    hideCommentsAndActivityLog?: boolean;
    /**
     * Renders the option as a non-editable view: the form is read-only, the Draft (Pending/
     * Unreleased) auto-edit is suppressed, and `onEdit` is a no-op. Used by
     * SelectionOptionDetailPanel for the allowance drawer, where the option is shown for
     * reference only.
     */
    readOnly?: boolean;
}

export const SelectionOptionInternal = (props: ISelectionOptionProps) => {
    const {
        history,
        jobId,
        jobName = "",
        stateOverride,
        selectionOptionId,
        isModal = false,
        onModalClose = null,
        selectionOptionGroupId,
        hideCommentsAndActivityLog = false,
        readOnly = false,
    } = props;
    const userInfo = useRequiredContext(UserInfoContext);
    const builderInfo = useRequiredContext(BuilderInfoContext);
    const webviewRouteParams = useWebviewContext();
    const selectionOptionRoutes = useSelectionOptionRoutes();

    // A new option won't have a selectionOptionId or it will be 0
    const isNewOption = !selectionOptionId;

    // Capture autofill state on first render — history.replace (called in useEffect) clears
    // location.state before SelectionOptionPresentational mounts (it's behind the loading gate).
    const initialAutofillStateRef = useRef(
        isNewOption ? (props.location?.state as ISelectionOptionAutofillState | null) : null,
    );

    // Capture the open-comments intent the list's comment icon pushed as router state, before the
    // consume-and-clear effect below wipes it. Passed to the presentational as a one-shot prop.
    const initialOpenCommentsRef = useRef(
        (props.location?.state as ISelectionOptionLocationState | null)?.openComments ?? false,
    );

    // Key to force form remount on Save and New
    const [formKey, setFormKey] = useState<number>(0);

    // Set when edit mode is manually started for existing options
    // Directly entering edit mode from the options list will be taken care
    // of in a later ticket
    const [isManuallyEditing, setIsManuallyEditing] = useState<boolean>(stateOverride === "Edit");

    const [showResetConfirm, setShowResetConfirm] = useState<boolean>(false);
    const [pendingResetValues, setPendingResetValues] = useState<ISelectionOptionFormValues | null>(
        null,
    );

    const hasSelectionDeletePermission = userInfo.permissions?.selections.canDelete;
    const hasSelectionUpdatePermission = userInfo.permissions?.selections.canUpdate;
    const hasSelectionCreatePermission = userInfo.permissions?.selections.canAdd;

    // force user back into view mode if they do not have edit permissions
    // really only a use case for users who manually edit the url to get into edit mode
    useEffect(() => {
        if (!hasSelectionUpdatePermission && isManuallyEditing) {
            setIsManuallyEditing(false);
        }
    }, [hasSelectionUpdatePermission, isManuallyEditing]);

    // Track net history pushes - if > 0 when back is clicked, some modal entries weren't popped (catalog
    // browsing, etc), so navigate directly to the list to avoid getting stuck in a loop.
    // If <= 0, everything evened out, so a standard goBack() is correct
    const historyDeltaRef = useRef(0);
    useEffect(() => {
        return history.listen((_, action) => {
            if (action === "PUSH") {
                historyDeltaRef.current++;
            } else if (action === "POP") {
                historyDeltaRef.current--;
            }
        });
    }, [history]);

    // The share-link redirect chain (EntityLinkRouter → JobPickerRouter) uses location.replace
    // and history.replace, leaving no real prior in-app entry. document.referrer ends up pointing
    // back through routes.entityLink.path (/link/:externalId), which is the only reliable signal
    // that this arrival was a share-link landing rather than an in-app navigation.
    const arrivedViaShareLinkRef = useRef(
        (() => {
            if (!document.referrer) return false;
            try {
                return routes.entityLink.parsePath(new URL(document.referrer).pathname) !== null;
            } catch {
                return false;
            }
        })(),
    );

    const navigate = useNavigate();

    // Define handleGoBack early so it can be used in query error handlers
    const handleGoBack = () => {
        if (isModal && onModalClose) {
            onModalClose();
        } else if (historyDeltaRef.current > 0) {
            // Net un-popped pushes remain — navigate directly to avoid looping through them
            navigate(selectionOptionRoutes.listLink(SelectionListContainerTabs.Default));
        } else if (arrivedViaShareLinkRef.current || history.length <= 1) {
            // Share-link landing or fresh tab — going back would leave the app or close the tab.
            navigate(selectionOptionRoutes.listLink(SelectionListContainerTabs.Default));
        } else {
            history.goBack();
        }
    };

    // New options will start in edit mode
    // Existing options will start in view mode until user changes it.
    // A read-only caller (e.g. the allowance drawer) can never enter edit mode.
    const isEditing = !readOnly && (isNewOption || isManuallyEditing);

    const { mutateAsync: createOption } = useSelectionOptionsCreateOptionPostV2();
    const { mutateAsync: deleteOption, isLoading: isDeleting } =
        useSelectionOptionsBySelectionOptionIdDeleteV2();
    const { mutateAsync: updateOption, isLoading: isUpdating } =
        useSelectionOptionsBySelectionOptionIdPutV2();
    const { mutateAsync: approveOptions } = useSelectionOptionsSelectionOptionsApprovePutV2();
    const { mutateAsync: declineOptions } = useSelectionOptionsSelectionOptionsDeclinePutV2();
    const { mutateAsync: resetOptions } = useSelectionOptionsSelectionOptionsResetPutV2();
    const { mutateAsync: sendOptions } = useSelectionOptionsSelectionOptionsSendPutV2();
    const { mutateAsync: copyOptions } = useSelectionOptionsCopyOptionsPostV2();
    const { mutateAsync: parseFromUrls, isLoading: isParsingProductUrl } =
        useSelectionOptionsParseFromUrlsPostV2();
    const { mutateAsync: saveItemsToCatalog, isLoading: isSavingToCatalog } =
        useSelectionCatalogSaveItemsPostV2();
    const { data: selectionOptionsDefaultsResponse, isLoading: isLoadingDefaults } =
        useSelectionOptionsSelectionOptionDefaultsByJobIdGetV2(jobId);

    const {
        data: selectionOptionEntity,
        isLoading: isLoadingOption,
        refetch: refetchSelectionOption,
    } = useSelectionOptionsBySelectionOptionIdGetV2(
        selectionOptionId ?? 0,
        { jobParam: jobId },
        {
            query: {
                enabled: !!selectionOptionId,
                retry: false,
                select: (response): ISelectionOptionEntity => ({
                    description: response.data?.description ?? "",
                    externalId: response.data?.externalId ?? "",
                    externalBuilderId: response.data?.externalBuilderId ?? "",
                    externalJobId: response.data?.externalJobId ?? "",
                    status: response.data?.status,
                    title: response.data?.title ?? "",
                    selectionOptionLineItems: Array.from(
                        response.data.lineItems?.values?.() ?? [],
                    ).map((x: LineItem) => new SelectionOptionLineItem(x as ILineItemResponse)),
                    lineItemDefaults: {
                        ...response.data.lineItemDefaults,
                    } as ILineItemDefaults,
                    selectionOptionGroups: response.data.selectionOptionGroups ?? [],
                    selectionOptionGroupId: response.data.selectionOptionGroupId ?? null,
                    categoryId: response.data.categoryId ?? null,
                    categories: mapCategoriesToBTSelectItems(
                        response.data.selectionCategories ?? [],
                        response.data.categoryId ?? null,
                    ),
                    locationId: response.data.locationId ?? null,
                    approvalByName: response.data.approvalByName ?? null,
                    approvalDate: response.data?.approvalDate ?? null,
                    addedByID: response.data?.addedByID,
                    addedByName: response.data.addedByName ?? null,
                    addedByDate: response.data?.addedByDate,
                    canCreatePurchaseOrder: response.data?.canCreatePurchaseOrder ?? false,
                    canAddToExistingPurchaseOrder:
                        response.data?.canAddToExistingPurchaseOrder ?? false,
                    productUrl: response.data?.productUrl ?? undefined,
                    imageUrl: response.data?.imageUrl ?? undefined,
                    deadlineDate: response.data?.deadlineDate ?? null,
                    taxGroupId: response.data?.taxGroupId ?? undefined,
                    effectiveTaxVersion: (response.data?.effectiveTaxVersion ?? undefined) as
                        | JobTaxVersion
                        | undefined,
                    linkedChangeOrderStatus: response.data?.linkedChangeOrderStatus ?? null,
                }),
                onError: (error) => {
                    showApixErrorMessage(
                        error,
                        "Selection option could not be loaded. It may have been deleted or your permissions have changed.",
                    );
                    // Navigate back - though this may be overridden by withJobsitesRequired modal architecture
                    handleGoBack();
                },
            },
        },
    );

    const { data: attachedFilesResponse, isLoading: isLoadingAttachments } =
        useSelectionOptionsAttachmentsBySelectionOptionIdGetV2(
            selectionOptionId ?? 0,
            {
                jobParam: jobId,
            },
            {
                query: {
                    enabled: !isNewOption,
                },
            },
        );

    const externalIdForDiscussions = selectionOptionEntity?.externalId ?? "";

    const { data: discussionResponse, refetch: refetchDiscussion } =
        useDiscussionsByExternalIdGetV2(
            externalIdForDiscussions,
            { linkedType: DiscussionsLinkedTypes.SelectionOptions },
            {
                query: {
                    enabled:
                        !isNewOption && !!externalIdForDiscussions && !hideCommentsAndActivityLog,
                },
            },
        );

    const discussion = discussionResponse?.data;
    const discussionEntity = useMemo<IHasDiscussionContainer | undefined>(() => {
        if (!discussion?.discussion) {
            return undefined;
        }
        return {
            discussions: new DiscussionsEntity(discussion.discussion),
            instanceKey: `selectionOption-${discussion.linkedId}`,
            id: discussion.linkedId,
            builderId: discussion.builderId,
            jobId: discussion.discussion.jobId ?? jobId,
        };
    }, [discussion, jobId]);

    const handleUpdateCommentData = useCallback(async () => {
        await refetchDiscussion();
    }, [refetchDiscussion]);

    const categoriesTreeData = useMemo(() => {
        const rawCategories = selectionOptionsDefaultsResponse?.data?.selectionCategories ?? [];
        const selectedId = selectionOptionEntity?.categoryId ?? null;
        return mapCategoriesToBTSelectItems(rawCategories, selectedId);
    }, [selectionOptionsDefaultsResponse, selectionOptionEntity?.categoryId]);

    const locationsTreeData = useMemo(() => {
        const rawLocations = selectionOptionsDefaultsResponse?.data?.locations ?? [];
        const selectedId = selectionOptionEntity?.locationId ?? null;
        return mapLocationsToBTSelectItems(rawLocations, selectedId);
    }, [selectionOptionsDefaultsResponse, selectionOptionEntity?.locationId]);

    // Clear location state after consuming it (autofill on create, open-comments on view) so
    // navigating back doesn't re-apply it. Both intents are captured into refs above first.
    const hasConsumedLocationStateRef = useRef(false);
    useEffect(() => {
        if (!hasConsumedLocationStateRef.current && props.location?.state) {
            hasConsumedLocationStateRef.current = true;
            props.history.replace(props.location.pathname, {});
        }
    }, [props.history, props.location?.state, props.location.pathname]);

    // new entities will be empty - don't use cached data when creating
    const entity = useMemo(() => {
        const costCodeIdsByGroupId =
            selectionOptionsDefaultsResponse?.data?.costCodeIdsBySelectionOptionGroupId;

        return selectionOptionId && selectionOptionEntity
            ? {
                  ...selectionOptionEntity,
                  costCodeIdsByGroupId,
                  categories: categoriesTreeData,
                  locations: locationsTreeData,
              }
            : {
                  title: "",
                  description: "",
                  selectionOptionLineItems: [],
                  status: Statuses.UNRELEASED,
                  lineItemDefaults: selectionOptionsDefaultsResponse?.data?.lineItemDefaults
                      ? (selectionOptionsDefaultsResponse.data
                            .lineItemDefaults as ILineItemDefaults)
                      : {
                            keysThatBreakCostLink: [],
                        },
                  costCodeIdsByGroupId,
                  selectionOptionGroups:
                      selectionOptionsDefaultsResponse?.data?.selectionOptionGroups ?? [],
                  selectionOptionGroupId:
                      selectionOptionGroupId && selectionOptionGroupId !== 0
                          ? selectionOptionGroupId
                          : null,
                  categoryId: null,
                  categories: categoriesTreeData,
                  locationId: null,
                  locations: locationsTreeData,
                  taxGroupId: selectionOptionsDefaultsResponse?.data?.taxGroupId ?? undefined,
                  effectiveTaxVersion: (selectionOptionsDefaultsResponse?.data
                      ?.effectiveTaxVersion ?? undefined) as JobTaxVersion | undefined,
              };
    }, [
        selectionOptionId,
        selectionOptionEntity,
        selectionOptionGroupId,
        selectionOptionsDefaultsResponse,
        categoriesTreeData,
        locationsTreeData,
    ]);

    const editRoute = useMemo(() => {
        return selectionOptionId && jobId
            ? selectionOptionRoutes.detailsLink(selectionOptionId, jobId, "Edit")
            : "";
    }, [selectionOptionId, jobId, selectionOptionRoutes]);

    const onEdit = useCallback(() => {
        if (readOnly || !hasSelectionUpdatePermission || isManuallyEditing) {
            return;
        }
        setIsManuallyEditing(true);
        if (!isModal) {
            navigate(editRoute, { replace: true });
        }
    }, [
        readOnly,
        hasSelectionUpdatePermission,
        isManuallyEditing,
        setIsManuallyEditing,
        navigate,
        editRoute,
        isModal,
    ]);

    // Draft (Pending/Unreleased) options auto-open in edit mode for editors — except when the
    // caller asks for a read-only view (e.g. the allowance drawer).
    useEffect(() => {
        if (
            !readOnly &&
            (selectionOptionEntity?.status === Statuses.PENDING ||
                selectionOptionEntity?.status === Statuses.UNRELEASED) &&
            hasSelectionUpdatePermission
        ) {
            onEdit();
        }
    }, [selectionOptionEntity?.status, hasSelectionUpdatePermission, onEdit, readOnly]);

    const onExitEditMode = () => {
        if (isManuallyEditing) {
            setIsManuallyEditing(false);
        } else if (isModal && onModalClose) {
            onModalClose();
        }
        if (!isModal) {
            selectionOptionId &&
                navigate(selectionOptionRoutes.detailsLink(selectionOptionId, jobId, "View"), {
                    replace: true,
                });
        }
    };

    const handleCreateOption = useCallback(
        async (
            request: SelectionOptionCreationRequest,
            action: SelectionOptionSaveAction,
            showToast: boolean = true,
        ) => {
            const response = await createOption({ data: request });

            if (response.status === 200 && response.data) {
                if (showToast) {
                    void message.success("Selection option created successfully");
                }
                const newSelectionOptionId = response.data.selectionOptionId;
                if (action === "saveAndNew") {
                    // Increment formKey to force form remount and clear all state
                    setFormKey((prev) => prev + 1);
                    return;
                } else if (action === "saveAndClose") {
                    navigate(selectionOptionRoutes.listLink(SelectionListContainerTabs.Default));
                } else if (isModal && onModalClose) {
                    onModalClose();
                } else {
                    navigate(
                        selectionOptionRoutes.detailsLink(newSelectionOptionId, jobId, "View"),
                        {
                            replace: true,
                        },
                    );
                }
            } else {
                void message.error("There was an error creating the selection option.");
            }
        },
        [createOption, navigate, selectionOptionRoutes, jobId, isModal, onModalClose, setFormKey],
    );

    const handleUpdateOption = useCallback(
        async (
            request: SelectionOptionCreationRequest,
            action: SelectionOptionSaveAction,
            showToast: boolean = true,
        ) => {
            const response = await updateOption({
                selectionOptionId: selectionOptionId!,
                data: request,
            });

            if (response.status === 200 && response.data) {
                if (showToast) {
                    void message.success("Selection option updated");
                }
                setIsManuallyEditing(false);
                if (action === "saveAndClose") {
                    navigate(selectionOptionRoutes.listLink(SelectionListContainerTabs.Default));
                }
                if (action === "saveAndNew") {
                    // Navigate to create mode - route change will reset the form
                    navigate(
                        selectionOptionRoutes.detailsLink(
                            0,
                            jobId,
                            "Create",
                            selectionOptionGroupId,
                        ),
                        { replace: true },
                    );
                    return;
                }
                if (action === "save") {
                    if (
                        response.data.status !== Statuses.PENDING &&
                        response.data.status !== Statuses.UNRELEASED
                    ) {
                        navigate(
                            selectionOptionRoutes.detailsLink(selectionOptionId!, jobId, "View"),
                            {
                                replace: true,
                            },
                        );
                    }
                }
            } else {
                void message.error("There was an error updating the Selection Option.");
            }
        },
        [
            updateOption,
            selectionOptionId,
            setIsManuallyEditing,
            navigate,
            selectionOptionRoutes,
            jobId,
            selectionOptionGroupId,
        ],
    );

    const handleCatalogSaveResponse = useCallback(
        async (selectionOptionIds: number[], didSaveBeforeCatalog: boolean): Promise<void> => {
            const response = await saveItemsToCatalog({
                data: { selectionOptionIds },
            });
            const successCount = response.data.savedSelectionCatalogOptionIds?.length ?? 0;
            const failureCount = selectionOptionIds.length - successCount;

            if (didSaveBeforeCatalog) {
                // Form was dirty, so we saved before catalog
                if (successCount === 1) {
                    void message.success("Selection option updated and saved to catalog");
                } else {
                    void message.warning(
                        "Selection option updated, but could not be saved to catalog",
                    );
                }
            } else {
                // Clean or readonly option, just show catalog outcome
                showSaveToCatalogOutcomeMessage({ successCount, failureCount }, "options");
            }
        },
        [saveItemsToCatalog],
    );

    const handleCatalogSaveFailure = useCallback(
        (catalogError: unknown, didSaveBeforeCatalog: boolean) => {
            if (didSaveBeforeCatalog) {
                // Form was dirty and saved, but catalog failed - show warning not error
                void message.warning("Selection option updated, but could not be saved to catalog");
                return;
            }

            // Clean option, catalog-only failure
            showApixErrorMessage(
                catalogError as Error,
                "Failed to save selection option to catalog",
            );
        },
        [],
    );

    const handleSave = useCallback(
        async (
            values: ISelectionOptionFormValues,
            action: SelectionOptionSaveAction,
            saveToCatalog: boolean = false,
            isDirty: boolean = false,
        ) => {
            try {
                const request = buildSelectionOptionRequest(values, jobId);
                let didSaveBeforeCatalog = false;

                // Stage 1: Save the option itself (create or update)
                try {
                    if (selectionOptionId) {
                        await handleUpdateOption(request, action, !saveToCatalog);
                    } else {
                        await handleCreateOption(request, action, !saveToCatalog);
                    }
                } catch (saveError: unknown) {
                    // If saveToCatalog was requested and save failed, show distinct error
                    if (saveToCatalog) {
                        showApixErrorMessage(
                            saveError as Error,
                            "Could not save changes; nothing was saved to catalog",
                        );
                    } else {
                        // Re-throw for outer catch to handle
                        throw saveError;
                    }
                    return;
                }

                // Stage 2: Save to catalog if requested
                if (saveToCatalog && selectionOptionId) {
                    didSaveBeforeCatalog = isEditing && isDirty;
                    const selectionOptionIds = [selectionOptionId];

                    try {
                        await handleCatalogSaveResponse(selectionOptionIds, didSaveBeforeCatalog);
                    } catch (catalogError: unknown) {
                        handleCatalogSaveFailure(catalogError, didSaveBeforeCatalog);
                    }
                }
            } catch (e) {
                showAPIErrorMessage(e, "Selection option could not be saved");
            }
        },
        [
            jobId,
            selectionOptionId,
            isEditing,
            handleUpdateOption,
            handleCreateOption,
            handleCatalogSaveResponse,
            handleCatalogSaveFailure,
        ],
    );

    const onSave = async (
        values: ISelectionOptionFormValues,
        action: SelectionOptionSaveAction,
    ) => {
        await handleSave(values, action, false);
    };

    // Wrapper to ensure Promise<void> for DeleteSelectionOption with correct signature
    const deleteOptionVoid = async (params: {
        selectionOptionId: number;
        params: { jobParam: number };
    }): Promise<void> => {
        await deleteOption(params);
    };

    const onDelete = async (selectionOptionId: number) => {
        if (!hasSelectionDeletePermission) {
            return;
        }
        await DeleteSelectionOption(selectionOptionId, jobId, deleteOptionVoid, handleGoBack);
    };

    const parseProductUrl = useCallback(
        async (
            url: string,
        ): Promise<{
            fields: ParsedUrlSelectionOptionFields;
            predictedCostCodeId: number | null;
        } | null> => {
            const trimmed = url.trim();
            if (!trimmed) return null;
            try {
                const response = await parseFromUrls({
                    data: { jobId, urls: [trimmed] },
                });
                const result = response.data.results?.[0];
                if (!result) {
                    void message.error(DEFAULT_SCRAPING_ERROR_MESSAGE);
                    return null;
                }
                if (result.status !== "success" || !result.fields) {
                    void message.error(result.errorMessage ?? DEFAULT_SCRAPING_ERROR_MESSAGE);
                    return null;
                }
                if (result.status === "success") {
                    void message.success(APPLY_PARSE_RESULT_TO_FORM_SUCCESS_MESSAGE);
                }
                return {
                    fields: result.fields,
                    predictedCostCodeId: result.predictedCostCodeId ?? null,
                };
            } catch (error: unknown) {
                showApixErrorMessage(error as Error, DEFAULT_SCRAPING_ERROR_MESSAGE);
                return null;
            }
        },
        [parseFromUrls, jobId],
    );

    const onSaveToCatalog = useCallback(
        async (values: ISelectionOptionFormValues, isDirty: boolean) => {
            if (!selectionOptionId) {
                return;
            }
            // Use unified handleSave with saveToCatalog=true and isDirty flag
            // This consolidates all save + catalog logic with proper error handling
            await handleSave(values, "save", true, isDirty);
        },
        [selectionOptionId, handleSave],
    );

    const onCopy = useCallback(async () => {
        try {
            const { jobId, selectionOptionId } = props;
            const isInAllowance = selectionOptionEntity?.selectionOptionGroupId != null;
            const response = await copyOptions({
                data: {
                    jobId,
                    selectionOptionIds: [selectionOptionId!],
                    allowanceCopyBehavior: (isInAllowance
                        ? AllowanceCopyBehavior.SameJobGroup
                        : AllowanceCopyBehavior.Standalone) as AllowanceCopyBehavior,
                },
            });
            const newOption = response.data.newOptions[0]?.newOption;
            if (newOption) {
                void message.success("Selection option copied");
                navigate(
                    selectionOptionRoutes.detailsLink(newOption.selectionOptionId, jobId, "View"),
                );
            } else {
                void message.error("There was an error copying the selection option");
            }
        } catch (error: unknown) {
            showApixErrorMessage(error as Error, "Failed to copy selection option");
        }
    }, [copyOptions, props, selectionOptionEntity, navigate, selectionOptionRoutes]);

    const performReset = async (values: ISelectionOptionFormValues) => {
        if (!selectionOptionId) {
            return;
        }

        try {
            // Call the shared reset helper which uses the batch endpoint
            const resetSuccess = await ResetSelectionOptions(
                builderInfo.builderId,
                jobId,
                [selectionOptionId],
                values.title ?? "Selection Option",
                resetOptions,
            );

            if (!resetSuccess) {
                return;
            }

            const queryKey = getSelectionOptionsBySelectionOptionIdGetV2QueryKey(selectionOptionId);
            await queryClient.invalidateQueries({ queryKey });

            // After recall the target status (Draft) is editable, so enter edit mode directly
            // instead of routing through View and relying on the auto-edit useEffect — that
            // indirect chain is susceptible to React 18 batched update timing and can leave
            // the page stuck in read-only state.
            setIsManuallyEditing(true);
            if (!isModal) {
                navigate(selectionOptionRoutes.detailsLink(selectionOptionId, jobId, "Edit"), {
                    replace: true,
                });
            }
        } catch (e) {
            showApixErrorMessage(e as Error, "Selection option could not be recalled");
        }
    };

    /**
     * Updates the entity first (if form is dirty), then changes status
     */
    const handleStatusChange = async (
        values: ISelectionOptionFormValues,
        statusChangeAction: "approve" | "decline" | "reset" | "send",
        loginType: BTLoginTypes,
        isDirty: boolean,
    ) => {
        if (
            !selectionOptionId ||
            (loginType === BTLoginTypes.BUILDER && !hasSelectionUpdatePermission)
        ) {
            return;
        }

        let actionTense: string;
        if (statusChangeAction === "approve") {
            actionTense = "approved";
        } else if (statusChangeAction === "decline") {
            actionTense = "declined";
        } else {
            actionTense = "sent";
        }

        try {
            // For reset, show custom dialog instead of utility confirmation —
            // except for Sent (PENDING) options, which recall straight to Draft without a prompt.
            if (statusChangeAction === "reset") {
                // Sent (PENDING) options recall straight to Draft without a confirmation prompt
                if (selectionOptionEntity?.status === Statuses.PENDING) {
                    await performReset(values);
                } else {
                    setPendingResetValues(values);
                    setShowResetConfirm(true);
                }
                return;
            }

            let statusEndpoint;
            if (statusChangeAction === "approve") {
                statusEndpoint = approveOptions;
            } else if (statusChangeAction === "decline") {
                statusEndpoint = declineOptions;
            } else {
                statusEndpoint = sendOptions;
            }

            // Send transitions Draft → Sent and doesn't need a confirmation; approve/decline do.
            if (statusChangeAction !== "send") {
                const userChoice = await showStatusChangeConfirmation(
                    statusChangeAction,
                    !!values.selectionOptionGroupId,
                );

                if (userChoice === "cancel") {
                    return;
                }
            }

            // If in edit mode and form is dirty, save form changes before submitting status change
            if (isEditing && isDirty) {
                const request = buildSelectionOptionRequest(values, jobId);
                await updateOption({
                    selectionOptionId,
                    data: request,
                });
            }

            // Change status using batch endpoint
            const statusResponse = await statusEndpoint({
                data: {
                    builderId: builderInfo.builderId,
                    jobId,
                    selectionOptionIds: [selectionOptionId],
                },
            });

            // Check for failures in batch response
            const failedCount = Number(statusResponse.data.failedSelectionOptions?.length ?? 0);
            if (failedCount > 0) {
                handleSelectionOptionActionMessages(
                    statusChangeAction,
                    values.title ?? "Selection Option",
                    0,
                    1,
                    [selectionOptionId],
                );
                return;
            }

            // Handle success using shared message utility
            handleSelectionOptionActionMessages(
                statusChangeAction,
                values.title ?? "Selection Option",
                1,
                0,
                [selectionOptionId],
            );

            await refreshAndNavigateToViewMode(
                selectionOptionId,
                jobId,
                navigate,
                setIsManuallyEditing,
                stateOverride,
                isModal,
                webviewRouteParams,
            );
        } catch (e) {
            showApixErrorMessage(e as Error, `Selection option could not be ${actionTense}`);
        }
    };

    const onApprove = async (
        values: ISelectionOptionFormValues,
        loginType: BTLoginTypes,
        isDirty: boolean,
    ) => {
        await handleStatusChange(values, "approve", loginType, isDirty);
    };

    const onDecline = async (
        values: ISelectionOptionFormValues,
        loginType: BTLoginTypes,
        isDirty: boolean,
    ) => {
        await handleStatusChange(values, "decline", loginType, isDirty);
    };

    const onReset = async (
        values: ISelectionOptionFormValues,
        loginType: BTLoginTypes,
        isDirty: boolean,
    ) => {
        await handleStatusChange(values, "reset", loginType, isDirty);
    };

    const onSend = async (
        values: ISelectionOptionFormValues,
        loginType: BTLoginTypes,
        isDirty: boolean,
    ) => {
        await handleStatusChange(values, "send", loginType, isDirty);
    };

    const handleConfirmReset = async () => {
        if (!pendingResetValues) {
            return;
        }
        const values = pendingResetValues;
        setShowResetConfirm(false);
        setPendingResetValues(null);
        await performReset(values);
    };

    const attachedFiles = useMemo(() => {
        if (attachedFilesResponse?.data) {
            return new AttachedFiles(attachedFilesResponse?.data);
        }

        if (selectionOptionsDefaultsResponse?.data.attachedFiles) {
            return new AttachedFiles(selectionOptionsDefaultsResponse?.data.attachedFiles);
        }

        return null;
    }, [attachedFilesResponse?.data, selectionOptionsDefaultsResponse?.data.attachedFiles]);

    // Show loading spinner when:
    // 1. Loading an existing option — wait for defaults, too, so categories/locations are ready before render
    // 2. Creating a new option with a selectionOptionGroupId while defaults are still loading
    const isExistingOptionLoading =
        !isNewOption && (isLoadingOption || isLoadingDefaults || isLoadingAttachments);
    const isNewOptionLoading = isNewOption && isLoadingDefaults;
    if (isExistingOptionLoading || isNewOptionLoading) {
        return <BTLoading />;
    }

    const isBuilder = userInfo.loginType === BTLoginTypes.BUILDER;
    const isExistingEntity = !!selectionOptionId;
    const canSaveToCatalog = isBuilder && isExistingEntity && Boolean(hasSelectionCreatePermission);

    return (
        <>
            <SelectionOptionPresentational
                key={`${selectionOptionId ?? 0}-${formKey}`}
                {...props}
                jobName={jobName ?? ""}
                builderInfo={builderInfo}
                isBuilder={isBuilder}
                isExistingEntity={isExistingEntity}
                entity={entity}
                attachedFiles={attachedFiles}
                hasDeletePermission={hasSelectionDeletePermission ?? false}
                hasUpdatePermission={hasSelectionUpdatePermission ?? false}
                hasCreatePermission={hasSelectionCreatePermission ?? false}
                isCreating={isNewOption}
                isDeleting={isDeleting}
                isEditing={isEditing && !isNewOption}
                isLoading={!!selectionOptionId && isLoadingOption}
                isModal={isModal}
                isUpdating={isUpdating}
                onDelete={onDelete}
                onEdit={onEdit}
                onGoBack={handleGoBack}
                onSave={onSave}
                openCreate={() => navigate(selectionOptionRoutes.detailsLink(0, jobId, "Create"))}
                openDetails={(selectionOptionId: number) =>
                    navigate(selectionOptionRoutes.detailsLink(selectionOptionId, jobId))
                }
                readOnly={!isEditing && !!selectionOptionId}
                onExitEditMode={onExitEditMode}
                onApprove={onApprove}
                onDecline={onDecline}
                onReset={onReset}
                onSend={onSend}
                onCopy={onCopy}
                parseProductUrl={parseProductUrl}
                isParsingProductUrl={isParsingProductUrl}
                initialAutofillState={isNewOption ? initialAutofillStateRef.current : null}
                openComments={initialOpenCommentsRef.current}
                canSaveToCatalog={canSaveToCatalog}
                onSaveToCatalog={onSaveToCatalog}
                isSavingToCatalog={isSavingToCatalog}
                onPoMappingComplete={() => {
                    refetchSelectionOption().catch(() => undefined);
                }}
                discussionEntity={discussionEntity}
                onUpdateCommentData={handleUpdateCommentData}
            />
            <ResetSelectionOptionConfirmDialog
                visible={showResetConfirm}
                selectionOptionCount={1}
                inSelectionOptionGroup={!!entity.selectionOptionGroupId}
                onConfirm={handleConfirmReset}
                onCancel={() => {
                    setShowResetConfirm(false);
                    setPendingResetValues(null);
                }}
            />
        </>
    );
};

const SelectionOptionWithJobPicker = withJobPrompt(SelectionOptionInternal, {
    routeTransform: (props) => {
        if (props.selectionOptionId) {
            return generatePath(props.match.url, {
                selectionOptionId: props.selectionOptionId,
                jobId: props.jobId,
                stateOverride: props.stateOverride,
            });
        } else {
            return routes.selection.getListLink(SelectionListContainerTabs.Default);
        }
    },
});

const SelectionOptionWithJobsRequired = withJobsitesRequired("Selection Options")(
    SelectionOptionWithJobPicker,
);

export default withErrorBoundary(SelectionOptionWithJobsRequired)(
    "Could not load selection option",
);
